import { useState } from "react";

import { MAX_ROWS, canPlace, groupKeyOf, isGroupId, isSystemId, isToolId, rowUsed, type Block, type Cell, type Item, type Where, type Width } from "./layout";

// Canvas da aba Ordem: blocos na ordem do portal (▲▼), cada um com linhas e
// tiles (largura 25/50/75/100). Um tile é um CAMPO editável (aba Campos), uma
// tool global ou um SUB-BLOCO (container: os campos arrastados pra dentro viram
// um card só no portal). DnD nativo: em dragover decide-se por
// e.dataTransfer.types (getData devolve "" no dragover); getData só no drop.

export const DT_SESSION = "text/ss-session";
export type Sel = { kind: "block" | "tool"; id: string } | { kind: "group"; id: string; blockKey: string } | null;

const has = (e: React.DragEvent, t: string) => Array.from(e.dataTransfer.types).includes(t);
const tile: React.CSSProperties = { background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 8, padding: "8px 10px", minWidth: 0, display: "flex", flexDirection: "column", gap: 4, cursor: "grab" };
const small: React.CSSProperties = { background: "none", border: "1px solid var(--line-strong)", borderRadius: 6, color: "var(--ink-secondary)", fontSize: 11, padding: "1px 6px", cursor: "pointer" };

export function BlockCanvas({ blocks, items, sel, onSelect, onSetWidth, onAddRow, onAddGroup, onRemoveGroup, onMoveBlock, onMoveCell, onUnplace }: {
  blocks: Block[]; items: Item[]; sel: Sel;
  onSelect: (s: Sel) => void;
  onSetWidth: (id: string, w: Width) => void;
  onAddRow: (at: Where) => void;
  onAddGroup: (blockKey: string) => void;
  onRemoveGroup: (blockKey: string, groupKey: string) => void;
  onMoveBlock: (blockKey: string, dir: -1 | 1) => void;
  onMoveCell: (id: string, to: Where & { rowIdx: number | "new"; beforeId?: string }) => void;
  onUnplace: (id: string) => void;
}) {
  const [over, setOver] = useState<string | null>(null);
  const byId = new Map(items.map((it) => [it.id, it]));

  const dropZone = (id: string, onDrop: (e: React.DragEvent) => void, style: React.CSSProperties, children?: React.ReactNode) => (
    <div key={id}
      onDragOver={(e) => { if (has(e, DT_SESSION)) { e.preventDefault(); e.stopPropagation(); if (over !== id) setOver(id); } }}
      onDragLeave={() => { if (over === id) setOver(null); }}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setOver(null); onDrop(e); }}
      style={{ ...style, outline: over === id ? "2px solid var(--brand)" : "none", background: over === id ? "var(--brand-soft)" : style.background }}
    >{children}</div>
  );

  /** Linhas de um container (bloco ou sub-bloco). */
  const renderRows = (b: Block, at: Where, rows: Cell[][]) => {
    const zone = (ri: number) => `${b.key}/${at.groupKey ?? ""}/${ri}`;
    const drop = (e: React.DragEvent, rowIdx: number | "new", beforeId?: string) => {
      const id = e.dataTransfer.getData(DT_SESSION);
      if (!id || id === beforeId) return;
      if (isGroupId(id) && at.groupKey) return;  // sub-bloco não entra em sub-bloco
      onMoveCell(id, { ...at, rowIdx, beforeId });
    };
    return (
      <>
        {rows.map((row, ri) => dropZone(zone(ri), (e) => drop(e, ri),
          { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8, minHeight: 46, padding: 4, borderRadius: 8, border: "1px dashed var(--line)" },
          <>
          {rowUsed(row) < 100 && (
            <div style={{ gridColumn: `${rowUsed(row) / 25 + 1} / span ${(100 - rowUsed(row)) / 25}`, gridRow: 1, border: "1px dashed var(--line-strong)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "var(--ink-dim)", pointerEvents: "none" }}>
              {100 - rowUsed(row)}% livre · solte aqui
            </div>
          )}
          {row.map((c) => {
            const id = c.session_id;
            const dragProps = {
              draggable: true,
              onDragStart: (e: React.DragEvent) => { e.stopPropagation(); e.dataTransfer.setData(DT_SESSION, id); e.dataTransfer.effectAllowed = "move"; },
              onDragOver: (e: React.DragEvent) => { if (has(e, DT_SESSION)) { e.preventDefault(); e.stopPropagation(); if (over !== id) setOver(id); } },
              onDragLeave: () => { if (over === id) setOver(null); },
              onDrop: (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setOver(null); drop(e, ri, id); },
            };
            if (isGroupId(id)) {
              const gk = groupKeyOf(id);
              const g = b.groups?.[gk];
              if (!g) return null;
              const on = sel?.kind === "group" && sel.id === id;
              // Soltar no cabeçalho/borda do sub-bloco = entrar nele (linha nova); sub-bloco sobre sub-bloco = reordenar fora.
              const groupDrop = {
                ...dragProps,
                onDrop: (e: React.DragEvent) => {
                  e.preventDefault(); e.stopPropagation(); setOver(null);
                  const did = e.dataTransfer.getData(DT_SESSION);
                  if (!did || did === id) return;
                  if (isGroupId(did)) drop(e, ri, id); else onMoveCell(did, { blockKey: b.key, groupKey: gk, rowIdx: "new" });
                },
              };
              return (
                <div key={id} {...groupDrop} style={{ gridColumn: "span 4", border: `1px solid ${over === id || on ? "var(--brand)" : "var(--warn-line)"}`, borderRadius: 10, background: "color-mix(in srgb, var(--warn-soft), transparent 70%)", padding: 10, display: "flex", flexDirection: "column", gap: 8, cursor: "grab" }}>
                  <div onClick={() => onSelect({ kind: "group", id, blockKey: b.key })} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <span style={{ color: "var(--ink-dim)", fontSize: 13 }}>⋮⋮</span>
                    <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 99, background: "var(--warn-soft)", color: "var(--warn)", border: "1px solid var(--warn-line)" }}>Sub-bloco</span>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.title || <span style={{ color: "var(--ink-muted)" }}>sem título · os campos de dentro viram um card só</span>}</span>
                    <button title="Excluir sub-bloco (campos voltam para a bandeja)" aria-label="Excluir sub-bloco" onClick={(e) => { e.stopPropagation(); onRemoveGroup(b.key, gk); }} style={small}>×</button>
                  </div>
                  {renderRows(b, { blockKey: b.key, groupKey: gk }, g.rows)}
                </div>
              );
            }
            const it = byId.get(id);
            if (!it) return null;
            const tool = isToolId(id);
            const sys = isSystemId(id);
            return (
              <div key={id} {...dragProps}
                onClick={() => { if (tool) onSelect({ kind: "tool", id }); }}
                style={{ ...tile, gridColumn: `span ${c.width / 25}`, borderColor: over === id || (sel && sel.id === id) ? "var(--brand)" : "var(--line)", cursor: tool ? "pointer" : "grab" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: "var(--ink-dim)", fontSize: 13 }}>⋮⋮</span>
                  {tool && <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 99, background: "var(--info-soft)", color: "var(--info)", border: "1px solid var(--info-line)" }}>Tool</span>}
                  {sys && <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 99, background: "var(--ok-soft)", color: "var(--ok)", border: "1px solid var(--ok-line)" }}>Sistema</span>}
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(c.portal?.title || it.name) || "Campo"}</span>
                  <button title="Tirar daqui (volta para a bandeja)" aria-label={`Tirar ${it.name} do portal`} onClick={(e) => { e.stopPropagation(); onUnplace(id); }} style={small}>×</button>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "var(--ink-muted)" }}>
                  <span style={{ flex: 1 }}>{tool ? "tool global" : it.type ?? "campo"}</span>
                  {!sys && <>
                    <button title="Menos largura" disabled={c.width <= 25} onClick={(e) => { e.stopPropagation(); onSetWidth(id, (c.width - 25) as Width); }} style={{ ...small, opacity: c.width <= 25 ? 0.35 : 1 }}>◂</button>
                    <span style={{ fontFamily: "var(--font-mono)", minWidth: 28, textAlign: "center" }}>{c.width}%</span>
                    <button title="Mais largura" disabled={c.width >= 100 || !canPlace(row, c.width + 25, id)} onClick={(e) => { e.stopPropagation(); onSetWidth(id, (c.width + 25) as Width); }} style={{ ...small, opacity: c.width >= 100 || !canPlace(row, c.width + 25, id) ? 0.35 : 1 }}>▸</button>
                  </>}
                </div>
              </div>
            );
          })}
          </>,
        ))}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {rows.filter((r) => r.length).length < MAX_ROWS && dropZone(zone(-1), (e) => drop(e, "new"),
            { flex: 1, border: "1px dashed var(--line)", borderRadius: 8, padding: "6px 10px", fontSize: 12, color: "var(--ink-dim)", textAlign: "center" },
            "solte aqui para nova linha",
          )}
          {rows.length < MAX_ROWS && <button onClick={() => onAddRow(at)} style={small}>+ Linha</button>}
          {!at.groupKey && <button onClick={() => onAddGroup(b.key)} style={{ ...small, borderColor: "var(--warn-line)", color: "var(--warn)" }}>+ Sub-bloco</button>}
          <span style={{ fontSize: 11, color: "var(--ink-dim)" }}>{rowUsed(rows[rows.length - 1] ?? [])}% na última linha</span>
        </div>
      </>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {blocks.length === 0 && <div style={{ fontSize: 13, color: "var(--ink-muted)", padding: 24, textAlign: "center", border: "1px dashed var(--line)", borderRadius: 10 }}>Nenhum bloco. Crie um em "+ Bloco" e arraste os campos da bandeja para dentro.</div>}
      {blocks.map((b, bi) => {
        const on = sel?.kind === "block" && sel.id === b.key;
        return (
          <div key={b.key} style={{ border: `1px solid ${on ? "var(--brand)" : "var(--line)"}`, borderRadius: 10, background: "var(--surface-hover)", padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
            <div onClick={() => onSelect({ kind: "block", id: b.key })} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--ink-dim)" }}>{bi + 1}.</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>{b.title || <span style={{ color: "var(--ink-muted)" }}>Bloco sem título</span>}</span>
              {b.description && <span style={{ fontSize: 12, color: "var(--ink-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>· {b.description}</span>}
              <span style={{ flex: 1 }} />
              <button title="Subir" disabled={bi === 0} onClick={(e) => { e.stopPropagation(); onMoveBlock(b.key, -1); }} style={{ ...small, opacity: bi === 0 ? 0.35 : 1 }}>▲</button>
              <button title="Descer" disabled={bi === blocks.length - 1} onClick={(e) => { e.stopPropagation(); onMoveBlock(b.key, 1); }} style={{ ...small, opacity: bi === blocks.length - 1 ? 0.35 : 1 }}>▼</button>
            </div>
            {renderRows(b, { blockKey: b.key }, b.rows)}
          </div>
        );
      })}
    </div>
  );
}
